export type DashboardRole = "student" | "parent" | "coach" | "academy";

export interface UserProfile {
  id: string;
  displayName: string;
  email: string;
  roles: readonly DashboardRole[];
  activeRole: DashboardRole;
  territory: string;
  rating?: number;
  token?: string;
  csrfToken?: string;
  isDemo?: boolean;
}

export const SYNTHETIC_PERSONAS: Record<DashboardRole, UserProfile> = {
  student: {
    id: "01918a20-0001-7000-8000-000000000001",
    displayName: "Arjun Kamalendran",
    email: "arjun.student@zhatura.com",
    roles: ["student"],
    activeRole: "student",
    territory: "IN-TN (Chennai)",
    rating: 1540,
    isDemo: true,
  },
  parent: {
    id: "01918a20-0002-7000-8000-000000000002",
    displayName: "Priya Kamalendran (Guardian)",
    email: "priya.parent@zhatura.com",
    roles: ["parent"],
    activeRole: "parent",
    territory: "IN-TN (Chennai)",
    isDemo: true,
  },
  coach: {
    id: "01918a20-0003-7000-8000-000000000003",
    displayName: "Coach Ramesh S. (FIDE Trainer)",
    email: "ramesh.coach@zhatura.com",
    roles: ["coach", "student"],
    activeRole: "coach",
    territory: "IN-TN (Coimbatore)",
    rating: 2320,
    isDemo: true,
  },
  academy: {
    id: "01918a20-0004-7000-8000-000000000004",
    displayName: "Tamil Nadu Chess Academy (Admin)",
    email: "admin@tnchessacademy.org",
    roles: ["academy"],
    activeRole: "academy",
    territory: "IN-TN (Statewide)",
    isDemo: true,
  },
};

interface AuthTokenResponse {
  access_token: string;
  account_id?: string;
}

interface PlayerProfileResponse {
  id: string;
  display_name: string;
}

const STORAGE_KEY = "zhatura_portal_user";

class SessionManager {
  private user: UserProfile | null = null;
  private listeners: Set<(user: UserProfile | null) => void> = new Set();

  constructor() {
    this.restore();
  }

  public subscribe(listener: (user: UserProfile | null) => void): () => void {
    this.listeners.add(listener);
    listener(this.user);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    for (const listener of this.listeners) {
      listener(this.user);
    }
  }

  public restore(): UserProfile | null {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.user = JSON.parse(stored) as UserProfile;
        return this.user;
      }
    } catch {
      // Storage unavailable or blocked
    }
    return null;
  }

  public getUser(): UserProfile | null {
    return this.user;
  }

  public isAuthenticated(): boolean {
    return this.user !== null;
  }

  public switchRole(role: DashboardRole): void {
    if (!this.user) return;
    this.user = {
      ...this.user,
      activeRole: role,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.user));
    } catch {
      // Ignore storage errors
    }
    this.notify();
  }

  public selectPersona(role: DashboardRole): UserProfile {
    const persona = SYNTHETIC_PERSONAS[role];
    this.user = { ...persona, isDemo: true };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.user));
    } catch {
      // Ignore storage errors
    }
    this.notify();
    return this.user;
  }

  public async loginWithCredentials(
    email: string,
    password: string,
  ): Promise<UserProfile> {
    // Attempt real API login against reverse proxy /api/v1/auth/tokens
    const response = await fetch("/api/v1/auth/tokens", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Request-ID": crypto.randomUUID(),
      },
      body: JSON.stringify({
        credential_type: "PASSWORD",
        email: email.trim(),
        password,
      }),
    });

    if (!response.ok) {
      throw new Error(`Authentication failed (HTTP ${response.status})`);
    }

    const data = (await response.json()) as AuthTokenResponse;
    // Fetch profile
    const profileResp = await fetch("/api/v1/players/me", {
      headers: {
        Authorization: `Bearer ${data.access_token}`,
      },
    });
    const profileData = profileResp.ok
      ? ((await profileResp.json()) as PlayerProfileResponse)
      : null;

    const role: DashboardRole = email.includes("coach")
      ? "coach"
      : email.includes("parent")
        ? "parent"
        : email.includes("academy")
          ? "academy"
          : "student";

    this.user = {
      id: data.account_id || profileData?.id || crypto.randomUUID(),
      displayName: profileData?.display_name || email.split("@")[0] || "User",
      email: email.trim(),
      roles: [role],
      activeRole: role,
      territory: "IN-TN",
      token: data.access_token,
      isDemo: false,
    };

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.user));
    this.notify();
    return this.user;
  }

  public logout(): void {
    this.user = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore
    }
    this.notify();
  }
}

export const session = new SessionManager();
